import type { ModuleTypeEnum } from '../../types/module-type'
import type {
  ImportSourcePreset,
  ImportSourcePresetId,
  ImportSourcePresetMatch,
} from './types'
import { normalizeImportHeader } from '../field-synonyms'
import { TIME_SOURCE_PRESETS } from './time-presets'
import { WORK_SOURCE_PRESETS } from './work-presets'

export * from './types'
export * from './time-presets'
export * from './work-presets'

export const IMPORT_SOURCE_PRESETS: readonly ImportSourcePreset[] = [
  ...TIME_SOURCE_PRESETS,
  ...WORK_SOURCE_PRESETS,
]

export function getImportSourcePreset(
  id: string | null | undefined,
): ImportSourcePreset | undefined {
  if (!id) return undefined
  return IMPORT_SOURCE_PRESETS.find(preset => preset.id === id)
}

function scorePreset(
  preset: ImportSourcePreset,
  normalizedHeaders: Set<string>,
): number | null {
  const satisfied = preset.fingerprint.requireAll.every(group =>
    group.some(header => normalizedHeaders.has(header)),
  )
  if (!satisfied) return null
  // How much of the FILE the preset explains (presets list several spellings
  // per column, so coverage of the preset itself would understate a match).
  let explained = 0
  for (const header of normalizedHeaders) {
    if (Object.prototype.hasOwnProperty.call(preset.columns, header))
      explained += 1
  }
  const distinctive = (preset.fingerprint.distinctive ?? []).filter(header =>
    normalizedHeaders.has(header),
  ).length
  // Coverage decides; distinctive columns break ties between presets that
  // share generic headers (Toggl vs Clockify both have Email/Start date).
  return explained / normalizedHeaders.size + distinctive * 0.01
}

/**
 * Recognize a known export from its header row. With `moduleType`, presets for
 * that module win; a file that plainly belongs elsewhere (a Harvest time export
 * opened in Projects) is still reported, flagged `moduleMismatch`, so the UI
 * can say where it belongs instead of mapping it badly.
 */
export function detectImportSourcePreset(
  headers: readonly string[],
  moduleType?: ModuleTypeEnum,
): ImportSourcePresetMatch | null {
  const normalized = new Set(
    headers.map(header => normalizeImportHeader(header)).filter(Boolean),
  )
  if (normalized.size === 0) return null

  let best: ImportSourcePresetMatch | null = null
  for (const preset of IMPORT_SOURCE_PRESETS) {
    const score = scorePreset(preset, normalized)
    if (score === null) continue
    const moduleMismatch = !!moduleType && preset.moduleType !== moduleType
    const candidate = { preset, score: Math.min(1, score), moduleMismatch }
    if (!best) {
      best = candidate
      continue
    }
    // A same-module match always beats a cross-module one.
    if (best.moduleMismatch !== moduleMismatch) {
      if (!moduleMismatch) best = candidate
      continue
    }
    if (score > best.score) best = candidate
  }
  return best
}

/**
 * What a preset says about one source header: a target field, `null` for
 * "deliberately left out", or `undefined` when the preset does not know the
 * header (ordinary auto-mapping decides).
 */
export function resolvePresetColumn(
  preset: ImportSourcePreset,
  header: string,
): string | null | undefined {
  const key = normalizeImportHeader(header)
  if (!Object.prototype.hasOwnProperty.call(preset.columns, key))
    return undefined
  return preset.columns[key]
}

/** The file's header holding the source record id, if the preset names one. */
export function findPresetExternalIdHeader(
  preset: ImportSourcePreset,
  headers: readonly string[],
): string | undefined {
  if (!preset.externalIdColumn) return undefined
  return headers.find(
    header => normalizeImportHeader(header) === preset.externalIdColumn,
  )
}

export function describeImportSourcePreset(
  preset: Pick<ImportSourcePreset, 'sourceLabel' | 'exportLabel'>,
): string {
  return `${preset.sourceLabel} ${preset.exportLabel}`
}

export function isImportSourcePresetId(
  value: unknown,
): value is ImportSourcePresetId {
  return (
    typeof value === 'string' &&
    IMPORT_SOURCE_PRESETS.some(preset => preset.id === value)
  )
}
